/**
 * 国优炬康 权限 + 数据范围 8 业务角色 × 50+ 二级菜单 × 7 层覆盖
 *
 * 详见:knowledge-base/08-对外合作/国优炬康/03-技术方案/研发交付方案/05-permission-and-data-scope.md
 *
 * 7 层覆盖:菜单 / 路由 / 按钮 / API / 数据范围 / 操作日志 / 越权页
 *
 * 8 业务角色 + 1 审计角色(只读 + 操作日志查询)
 */

import { AdminRole } from '../enums';

// ───── 一级业务域(8 个) ─────
export enum AdminModule {
  DASHBOARD = 'DASHBOARD', // 工作台
  CONTENT = 'CONTENT', // 内容运营
  MEMBER = 'MEMBER', // 会员
  GOODS = 'GOODS', // 商品
  ORDER = 'ORDER', // 订单
  FINANCE = 'FINANCE', // 财务
  HEALTH = 'HEALTH', // 健康
  SYSTEM = 'SYSTEM', // 系统
}

export const AdminModuleName: Record<AdminModule, string> = {
  [AdminModule.DASHBOARD]: '工作台',
  [AdminModule.CONTENT]: '内容运营',
  [AdminModule.MEMBER]: '会员',
  [AdminModule.GOODS]: '商品',
  [AdminModule.ORDER]: '订单',
  [AdminModule.FINANCE]: '财务',
  [AdminModule.HEALTH]: '健康',
  [AdminModule.SYSTEM]: '系统',
};

// ───── 二级菜单权限点(跨 8 个一级模块) ─────
export enum AdminPermission {
  // 工作台
  DASHBOARD_VIEW = 'DASHBOARD_VIEW',
  DASHBOARD_STATS = 'DASHBOARD_STATS',
  DASHBOARD_SWITCH_ROLE = 'DASHBOARD_SWITCH_ROLE',

  // 内容运营
  CONTENT_BANNER_LIST = 'CONTENT_BANNER_LIST',
  CONTENT_BANNER_EDIT = 'CONTENT_BANNER_EDIT',
  CONTENT_ARTICLE_LIST = 'CONTENT_ARTICLE_LIST',
  CONTENT_ARTICLE_EDIT = 'CONTENT_ARTICLE_EDIT',
  CONTENT_ARTICLE_REVIEW = 'CONTENT_ARTICLE_REVIEW',
  CONTENT_EXPERT_LIST = 'CONTENT_EXPERT_LIST',
  CONTENT_EXPERT_EDIT = 'CONTENT_EXPERT_EDIT',
  CONTENT_HEALTH_COACH_LIST = 'CONTENT_HEALTH_COACH_LIST', // 健康管理师(B-02)
  CONTENT_HEALTH_COACH_EDIT = 'CONTENT_HEALTH_COACH_EDIT',
  CONTENT_CONSULT_LIST = 'CONTENT_CONSULT_LIST',
  CONTENT_CONSULT_REPLY = 'CONTENT_CONSULT_REPLY',
  CONTENT_SQUARE_LIST = 'CONTENT_SQUARE_LIST',
  CONTENT_SQUARE_REVIEW = 'CONTENT_SQUARE_REVIEW',
  CONTENT_VIDEO_COMMENT_LIST = 'CONTENT_VIDEO_COMMENT_LIST',
  CONTENT_REVIEW_LIST = 'CONTENT_REVIEW_LIST',
  CONTENT_LIVE_LIST = 'CONTENT_LIVE_LIST',

  // 会员
  MEMBER_LIST = 'MEMBER_LIST',
  MEMBER_DETAIL = 'MEMBER_DETAIL',
  MEMBER_LEVEL_EDIT = 'MEMBER_LEVEL_EDIT',
  MEMBER_FAMILY_LIST = 'MEMBER_FAMILY_LIST',
  MEMBER_POINTS_EDIT = 'MEMBER_POINTS_EDIT',
  MEMBER_BADGE_EDIT = 'MEMBER_BADGE_EDIT',

  // 商品
  GOODS_CATEGORY_LIST = 'GOODS_CATEGORY_LIST',
  GOODS_CATEGORY_EDIT = 'GOODS_CATEGORY_EDIT',
  GOODS_PRODUCT_LIST = 'GOODS_PRODUCT_LIST',
  GOODS_PRODUCT_EDIT = 'GOODS_PRODUCT_EDIT',
  GOODS_SHIPPING_TEMPLATE_LIST = 'GOODS_SHIPPING_TEMPLATE_LIST',
  GOODS_STOCK_LIST = 'GOODS_STOCK_LIST',
  GOODS_STOCK_EDIT = 'GOODS_STOCK_EDIT',
  GOODS_REVIEW_LIST = 'GOODS_REVIEW_LIST',

  // 订单
  ORDER_LIST = 'ORDER_LIST',
  ORDER_DETAIL = 'ORDER_DETAIL',
  ORDER_SHIP = 'ORDER_SHIP',
  ORDER_REFUND_LIST = 'ORDER_REFUND_LIST',
  ORDER_REFUND_AUDIT = 'ORDER_REFUND_AUDIT',
  ORDER_LOGISTICS_LIST = 'ORDER_LOGISTICS_LIST',
  ORDER_AFTER_SALE_LIST = 'ORDER_AFTER_SALE_LIST',

  // 财务
  FIN_PAYMENT_LIST = 'FIN_PAYMENT_LIST',
  FIN_REFUND_LIST = 'FIN_REFUND_LIST',
  FIN_COMMISSION_LIST = 'FIN_COMMISSION_LIST',
  FIN_COMMISSION_REVERSE = 'FIN_COMMISSION_REVERSE',
  FIN_WITHDRAWAL_LIST = 'FIN_WITHDRAWAL_LIST',
  FIN_WITHDRAWAL_AUDIT = 'FIN_WITHDRAWAL_AUDIT',
  FIN_WITHDRAWAL_PAY = 'FIN_WITHDRAWAL_PAY',

  // 健康
  HEALTH_RECORD_LIST = 'HEALTH_RECORD_LIST',
  HEALTH_RECORD_DETAIL = 'HEALTH_RECORD_DETAIL',
  HEALTH_RECORD_EXPORT = 'HEALTH_RECORD_EXPORT',
  HEALTH_DEVICE_LIST = 'HEALTH_DEVICE_LIST',
  HEALTH_CONSENT_LIST = 'HEALTH_CONSENT_LIST',
  HEALTH_DIET_AI_LIST = 'HEALTH_DIET_AI_LIST',
  HEALTH_VOLUNTEER_LIST = 'HEALTH_VOLUNTEER_LIST',
  HEALTH_VOLUNTEER_EDIT = 'HEALTH_VOLUNTEER_EDIT',
  HEALTH_VOLUNTEER_REVIEW = 'HEALTH_VOLUNTEER_REVIEW', // 志愿者报名审核
  HEALTH_FOOD_LIST = 'HEALTH_FOOD_LIST', // 食物库
  HEALTH_FOOD_EDIT = 'HEALTH_FOOD_EDIT',
  HEALTH_RECIPE_LIST = 'HEALTH_RECIPE_LIST', // 食谱
  HEALTH_RECIPE_EDIT = 'HEALTH_RECIPE_EDIT',
  HEALTH_ALERT_LIST = 'HEALTH_ALERT_LIST', // 健康预警
  HEALTH_ALERT_HANDLE = 'HEALTH_ALERT_HANDLE',
  HEALTH_PATIENT_LIST = 'HEALTH_PATIENT_LIST', // 医患(患者绑定/交流)
  HEALTH_PATIENT_BINDING = 'HEALTH_PATIENT_BINDING',
  HEALTH_MEMBER_HEALTH_VIEW = 'HEALTH_MEMBER_HEALTH_VIEW', // 会员健康 360(汇总视图)

  // 系统
  SYS_ADMIN_LIST = 'SYS_ADMIN_LIST',
  SYS_ROLE_EDIT = 'SYS_ROLE_EDIT',
  SYS_PERMISSION_EDIT = 'SYS_PERMISSION_EDIT',
  SYS_AUDIT_LOG = 'SYS_AUDIT_LOG',
  SYS_THIRD_PARTY_CONFIG = 'SYS_THIRD_PARTY_CONFIG',
  SYS_PAGE_EDIT = 'SYS_PAGE_EDIT', // 单页文档(用户协议/隐私政策)编辑
  SYS_BRAND_CONFIG = 'SYS_BRAND_CONFIG', // 品牌设置(logo/标题/主题色)
}

/**
 * 8 角色 × 权限点 映射表
 *
 * 规则:
 * - ADMIN(超管):全部权限点
 * - OPS(运营):内容运营 + 会员基础 + 商品查看 + 订单查看 + 工作台
 * - GOODS(商品):商品全 + 工作台 + 内容查看
 * - ORDER(订单):订单全 + 售后 + 工作台 + 商品查看 + 会员查看
 * - FIN(财务):财务全 + 订单查看 + 工作台 + 退款审核
 * - CONTENT(内容):内容全 + 工作台 + 会员查看 + 专家
 * - HEALTH(健康):健康全 + 工作台 + 会员查看 + 志愿者
 * - VOL(志愿者):志愿者 + 报名审核 + 工作台查看
 * - AUDIT(审计):全部只读 + SYS_AUDIT_LOG(view only)
 */
export const RolePermissions: Record<AdminRole, ReadonlySet<AdminPermission>> = {
  [AdminRole.ADMIN]: new Set(Object.values(AdminPermission)),

  [AdminRole.OPS]: new Set([
    AdminPermission.DASHBOARD_VIEW,
    AdminPermission.DASHBOARD_STATS,
    AdminPermission.CONTENT_BANNER_LIST,
    AdminPermission.CONTENT_BANNER_EDIT,
    AdminPermission.CONTENT_ARTICLE_LIST,
    AdminPermission.CONTENT_ARTICLE_REVIEW,
    AdminPermission.CONTENT_EXPERT_LIST,
    AdminPermission.CONTENT_SQUARE_LIST,
    AdminPermission.CONTENT_SQUARE_REVIEW,
    AdminPermission.CONTENT_VIDEO_COMMENT_LIST,
    AdminPermission.CONTENT_REVIEW_LIST,
    AdminPermission.CONTENT_LIVE_LIST,
    AdminPermission.CONTENT_HEALTH_COACH_LIST,
    AdminPermission.MEMBER_LIST,
    AdminPermission.MEMBER_FAMILY_LIST,
    AdminPermission.GOODS_CATEGORY_LIST,
    AdminPermission.GOODS_PRODUCT_LIST,
    AdminPermission.GOODS_SHIPPING_TEMPLATE_LIST,
    AdminPermission.GOODS_REVIEW_LIST,
    AdminPermission.GOODS_STOCK_LIST,
    AdminPermission.ORDER_LIST,
    AdminPermission.ORDER_DETAIL,
    AdminPermission.HEALTH_VOLUNTEER_LIST,
  ]),

  [AdminRole.GOODS]: new Set([
    AdminPermission.DASHBOARD_VIEW,
    AdminPermission.DASHBOARD_STATS,
    AdminPermission.GOODS_CATEGORY_LIST,
    AdminPermission.GOODS_CATEGORY_EDIT,
    AdminPermission.GOODS_PRODUCT_LIST,
    AdminPermission.GOODS_SHIPPING_TEMPLATE_LIST,
    AdminPermission.GOODS_PRODUCT_EDIT,
    AdminPermission.GOODS_STOCK_LIST,
    AdminPermission.GOODS_STOCK_EDIT,
    AdminPermission.GOODS_REVIEW_LIST,
    AdminPermission.ORDER_LIST,
    AdminPermission.MEMBER_LIST,
    AdminPermission.CONTENT_ARTICLE_LIST,
  ]),

  [AdminRole.ORDER]: new Set([
    AdminPermission.DASHBOARD_VIEW,
    AdminPermission.DASHBOARD_STATS,
    AdminPermission.ORDER_LIST,
    AdminPermission.ORDER_DETAIL,
    AdminPermission.ORDER_SHIP,
    AdminPermission.ORDER_REFUND_LIST,
    AdminPermission.ORDER_LOGISTICS_LIST,
    AdminPermission.ORDER_AFTER_SALE_LIST,
    AdminPermission.GOODS_PRODUCT_LIST,
    AdminPermission.GOODS_SHIPPING_TEMPLATE_LIST,
    AdminPermission.MEMBER_LIST,
    AdminPermission.MEMBER_DETAIL,
    AdminPermission.FIN_REFUND_LIST,
  ]),

  [AdminRole.FIN]: new Set([
    AdminPermission.DASHBOARD_VIEW,
    AdminPermission.DASHBOARD_STATS,
    AdminPermission.FIN_PAYMENT_LIST,
    AdminPermission.FIN_REFUND_LIST,
    AdminPermission.FIN_COMMISSION_LIST,
    AdminPermission.FIN_COMMISSION_REVERSE,
    AdminPermission.FIN_WITHDRAWAL_LIST,
    AdminPermission.FIN_WITHDRAWAL_AUDIT,
    AdminPermission.FIN_WITHDRAWAL_PAY,
    AdminPermission.ORDER_LIST,
    AdminPermission.ORDER_DETAIL,
    AdminPermission.ORDER_REFUND_AUDIT,
    AdminPermission.MEMBER_LIST,
    AdminPermission.GOODS_PRODUCT_LIST,
    AdminPermission.GOODS_SHIPPING_TEMPLATE_LIST,
  ]),

  [AdminRole.CONTENT]: new Set([
    AdminPermission.DASHBOARD_VIEW,
    AdminPermission.DASHBOARD_STATS,
    AdminPermission.CONTENT_BANNER_LIST,
    AdminPermission.CONTENT_BANNER_EDIT,
    AdminPermission.CONTENT_ARTICLE_LIST,
    AdminPermission.CONTENT_ARTICLE_EDIT,
    AdminPermission.CONTENT_ARTICLE_REVIEW,
    AdminPermission.CONTENT_EXPERT_LIST,
    AdminPermission.CONTENT_EXPERT_EDIT,
    AdminPermission.CONTENT_HEALTH_COACH_LIST,
    AdminPermission.CONTENT_HEALTH_COACH_EDIT,
    AdminPermission.CONTENT_CONSULT_LIST,
    AdminPermission.CONTENT_CONSULT_REPLY,
    AdminPermission.CONTENT_SQUARE_LIST,
    AdminPermission.CONTENT_SQUARE_REVIEW,
    AdminPermission.CONTENT_VIDEO_COMMENT_LIST,
    AdminPermission.CONTENT_REVIEW_LIST,
    AdminPermission.CONTENT_LIVE_LIST,
    AdminPermission.MEMBER_LIST,
    AdminPermission.MEMBER_DETAIL,
  ]),

  // 健康管理师最小权限集(2026-08-09 与永哥确认):绑定患者/看数据/处理预警/调方案;
  // 不含导出(会上明确仅超管)、志愿者、设备/食谱/食物库等运营配置项
  [AdminRole.HEALTH]: new Set([
    AdminPermission.DASHBOARD_VIEW,
    AdminPermission.DASHBOARD_STATS,
    AdminPermission.HEALTH_RECORD_LIST,
    AdminPermission.HEALTH_RECORD_DETAIL,
    AdminPermission.HEALTH_ALERT_LIST,
    AdminPermission.HEALTH_ALERT_HANDLE,
    AdminPermission.HEALTH_PATIENT_LIST,
    AdminPermission.HEALTH_PATIENT_BINDING,
    AdminPermission.HEALTH_MEMBER_HEALTH_VIEW,
    AdminPermission.MEMBER_LIST,
    AdminPermission.MEMBER_DETAIL,
  ]),

  // 医生(2026-08-12 与永哥确认):能力对齐健康管理师(绑定患者/看数据/处理预警),
  // 另加咨询列表/答复权限(问医生走专家咨询入口,按 expert.admin_user_id 关联可见);
  // 注意:含 CONTENT_CONSULT_* 会派生 CONTENT 域,BOUND 判定已改为"含 HEALTH 且不含 ADMIN/OPS"
  // 2026-08-12 永哥:医生不需要会员列表菜单(去掉 MEMBER_LIST;会员健康页内搜索走 HEALTH 域鉴权,不受影响)
  [AdminRole.DOCTOR]: new Set([
    AdminPermission.DASHBOARD_VIEW,
    AdminPermission.DASHBOARD_STATS,
    AdminPermission.HEALTH_RECORD_LIST,
    AdminPermission.HEALTH_RECORD_DETAIL,
    AdminPermission.HEALTH_ALERT_LIST,
    AdminPermission.HEALTH_ALERT_HANDLE,
    AdminPermission.HEALTH_PATIENT_LIST,
    AdminPermission.HEALTH_PATIENT_BINDING,
    AdminPermission.HEALTH_MEMBER_HEALTH_VIEW,
    AdminPermission.MEMBER_DETAIL,
    AdminPermission.CONTENT_CONSULT_LIST,
    AdminPermission.CONTENT_CONSULT_REPLY,
  ]),

  [AdminRole.VOL]: new Set([
    AdminPermission.DASHBOARD_VIEW,
    AdminPermission.HEALTH_VOLUNTEER_LIST,
    AdminPermission.HEALTH_VOLUNTEER_EDIT,
    AdminPermission.HEALTH_VOLUNTEER_REVIEW,
  ]),

  // 审计角色:全部只读(view-only 强制),仅 SYS_AUDIT_LOG 可操作
  [AdminRole.AUDIT]: new Set([
    AdminPermission.DASHBOARD_VIEW,
    AdminPermission.MEMBER_LIST,
    AdminPermission.MEMBER_DETAIL,
    AdminPermission.MEMBER_FAMILY_LIST,
    AdminPermission.GOODS_PRODUCT_LIST,
    AdminPermission.GOODS_SHIPPING_TEMPLATE_LIST,
    AdminPermission.GOODS_CATEGORY_LIST,
    AdminPermission.GOODS_STOCK_LIST,
    AdminPermission.ORDER_LIST,
    AdminPermission.ORDER_DETAIL,
    AdminPermission.ORDER_REFUND_LIST,
    AdminPermission.ORDER_LOGISTICS_LIST,
    AdminPermission.ORDER_AFTER_SALE_LIST,
    AdminPermission.FIN_PAYMENT_LIST,
    AdminPermission.FIN_REFUND_LIST,
    AdminPermission.FIN_COMMISSION_LIST,
    AdminPermission.FIN_WITHDRAWAL_LIST,
    AdminPermission.HEALTH_RECORD_LIST,
    AdminPermission.HEALTH_DEVICE_LIST,
    AdminPermission.HEALTH_CONSENT_LIST,
    AdminPermission.HEALTH_ALERT_LIST,
    AdminPermission.HEALTH_FOOD_LIST,
    AdminPermission.HEALTH_RECIPE_LIST,
    AdminPermission.HEALTH_PATIENT_LIST,
    AdminPermission.HEALTH_MEMBER_HEALTH_VIEW,
    AdminPermission.SYS_AUDIT_LOG,
  ]),
};

// ───── 数据范围规则 ─────

/**
 * 数据范围类型
 */
export enum DataScope {
  ALL = 'ALL', // 全部数据
  SELF = 'SELF', // 仅本人
  DEPARTMENT = 'DEPARTMENT', // 本部门
  CUSTOM = 'CUSTOM', // 自定义(后台健康角色按患者绑定)
}

/**
 * 关键数据范围规则
 */
export const DataScopeRules = {
  // 会员基础资料:ADMIN/OPS 可见,手机号脱敏
  MEMBER_BASIC: {
    roles: [AdminRole.ADMIN, AdminRole.OPS, AdminRole.AUDIT],
    maskPhone: true,
    scope: DataScope.ALL,
  },
  // 会员手机号:仅 ADMIN
  MEMBER_PHONE: {
    roles: [AdminRole.ADMIN, AdminRole.AUDIT],
    maskPhone: false,
    scope: DataScope.ALL,
  },
  // 健康档案:HEALTH/DOCTOR 仅授权关系内;ADMIN 全部(留痕)
  HEALTH_RECORD: {
    roles: [AdminRole.HEALTH, AdminRole.DOCTOR, AdminRole.ADMIN, AdminRole.AUDIT],
    maskPhone: false,
    scope: DataScope.CUSTOM, // 按患者绑定
    requireConsent: true,
  },
  // 退款数据:ORDER/FIN/ADMIN/AUDIT
  REFUND: {
    roles: [AdminRole.ORDER, AdminRole.FIN, AdminRole.ADMIN, AdminRole.AUDIT],
    maskPhone: false,
    scope: DataScope.ALL,
  },
  // 佣金/提现:FIN/ADMIN/AUDIT
  COMMISSION_WITHDRAWAL: {
    roles: [AdminRole.FIN, AdminRole.ADMIN, AdminRole.AUDIT],
    maskPhone: false,
    scope: DataScope.ALL,
  },
  // 操作日志:仅 ADMIN/AUDIT
  AUDIT_LOG: {
    roles: [AdminRole.ADMIN, AdminRole.AUDIT],
    maskPhone: false,
    scope: DataScope.ALL,
  },
} as const;

// ───── 工具函数 ─────

export function hasPermission(role: AdminRole, permission: AdminPermission): boolean {
  return RolePermissions[role].has(permission);
}

export function getRolePermissions(role: AdminRole): AdminPermission[] {
  return RolePermissions[role] ? Array.from(RolePermissions[role]) : [];
}

/** 权限码前缀 → 接口域角色编码(SYS_ 无对应域,系统页仅超管) */
const PERMISSION_PREFIX_TO_DOMAIN: Record<string, string> = {
  DASHBOARD: 'DASHBOARD',
  CONTENT: 'CONTENT',
  MEMBER: 'MEMBER',
  GOODS: 'GOODS',
  ORDER: 'ORDER',
  FIN: 'FIN',
  HEALTH: 'HEALTH',
};

/**
 * 从权限点集合推导接口访问域:勾了哪个域的页面权限,就拥有该域的接口访问权。
 * 角色配置页不再需要人工选择"接口访问域"概念。
 */
export function deriveRoleDomains(permissions: string[]): string[] {
  const domains = new Set<string>();
  for (const permission of permissions) {
    const domain = PERMISSION_PREFIX_TO_DOMAIN[String(permission).split('_')[0]];
    if (domain) domains.add(domain);
  }
  return [...domains];
}

export function isViewOnly(role: AdminRole): boolean {
  return role === AdminRole.AUDIT;
}
